import React from 'react';
import { BACKGROUNDS, normalizeAvatar } from '../../shared/avatar';
import { tierForRating, nextTier, tierProgress } from '../lib/rating';
import { Avatar } from './Avatar';

/**
 * Cabecera de un perfil, propio o ajeno.
 *
 * La portada toma los colores del fondo del avatar: así cada perfil tiene su
 * propio color sin pedirle nada más a nadie, y el personaje parece salir de
 * ella. El rango y el D-Rating van a la vista, con la barra hacia el
 * siguiente rango dentro de la misma tarjeta.
 */
export const ProfileHero: React.FC<{
  avatar: unknown;
  username: string;
  /** Línea bajo el nombre: país, edad, desde cuándo. */
  details: (string | null | undefined)[];
  dRating: number;
  ratingLabel?: string;
  /** Botones a la derecha (editar perfil, agregar amigo). */
  actions?: React.ReactNode;
  /** Contenido extra bajo el nombre (p. ej. el botón de amistad). */
  children?: React.ReactNode;
}> = ({ avatar, username, details, dRating, ratingLabel = 'D-Rating', actions, children }) => {
  const config = normalizeAvatar(avatar);
  const bg = BACKGROUNDS.find((b) => b.id === config.background) ?? BACKGROUNDS[0];
  const tier = tierForRating(dRating);
  const next = nextTier(dRating);
  const progress = tierProgress(dRating);

  return (
    <div className="surface-raised overflow-hidden">
      {/* Portada */}
      <div
        className="relative h-28 sm:h-32"
        style={{ background: `linear-gradient(135deg, ${bg.from} 0%, ${bg.to} 100%)` }}
        aria-hidden="true"
      >
        {/* Signos de interrogación sueltos, como el cuerno del logotipo. */}
        <svg className="absolute inset-0 w-full h-full opacity-20" preserveAspectRatio="xMidYMid slice" viewBox="0 0 400 120">
          {[
            [30, 40, 26, -12],
            [110, 95, 18, 10],
            [190, 30, 22, 6],
            [270, 85, 30, -8],
            [350, 35, 20, 14],
            [380, 100, 14, -4],
          ].map(([x, y, size, rot]) => (
            <text
              key={`${x}-${y}`}
              x={x}
              y={y}
              fontSize={size}
              fontWeight="900"
              fill="#FFFFFF"
              transform={`rotate(${rot} ${x} ${y})`}
              fontFamily="Outfit, sans-serif"
            >
              ?
            </text>
          ))}
        </svg>
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/15 to-transparent" />
      </div>

      <div className="px-5 sm:px-6 pb-5">
        <div className="flex items-end gap-4 flex-wrap -mt-12 sm:-mt-14">
          <div className="rounded-[30%] p-1 bg-ink-900 shadow-xl shrink-0">
            <Avatar config={avatar} size={104} className="shadow-none" animate />
          </div>
          <div className="flex-1 min-w-[10rem] pb-1 pt-14 sm:pt-16">
            <h1 className="text-2xl sm:text-3xl font-display font-black text-ink-50 leading-tight break-words">
              {username}
            </h1>
          </div>
          {actions && <div className="flex gap-2 flex-wrap pb-1 w-full sm:w-auto">{actions}</div>}
        </div>

        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className={`chip ${tier.chip} ${tier.text}`}>
            {tier.icon} {tier.label}
          </span>
          {details.some(Boolean) && (
            <span className="text-ink-400 text-sm">{details.filter(Boolean).join(' · ')}</span>
          )}
          {children}
        </div>

        {/* D-Rating y camino al siguiente rango */}
        <div className="mt-4 flex items-center gap-4 rounded-2xl bg-ink-850 border border-ink-700 p-3.5 sm:p-4">
          <div className="shrink-0">
            <p className="text-ink-500 text-[0.6875rem] uppercase tracking-wider">{ratingLabel}</p>
            <p className="text-3xl sm:text-4xl font-display font-black font-mono tabular-nums text-brand-gradient leading-none mt-1">
              {dRating}
            </p>
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            {next ? (
              <>
                <div className="flex justify-between gap-2 text-xs">
                  <span className="text-ink-300 truncate">
                    Siguiente: {next.icon} {next.label}
                  </span>
                  <span className="text-ink-500 font-mono shrink-0">faltan {next.min - dRating}</span>
                </div>
                <div className="h-2.5 rounded-full bg-ink-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-gradient transition-[width] duration-700"
                    style={{ width: `${Math.max(4, progress * 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-ink-300">Rango máximo alcanzado. {tier.icon}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TONES = {
  sky: 'bg-sky-500/15 text-sky-400',
  success: 'bg-success-500/15 text-success-400',
  ember: 'bg-ember-500/15 text-ember-400',
  magenta: 'bg-magenta-500/15 text-magenta-300',
  violet: 'bg-violet-500/15 text-violet-500',
} as const;

/** Una cifra del perfil con su icono en color. */
export const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone: keyof typeof TONES;
}> = ({ icon, label, value, sub, tone }) => (
  <div className="surface p-4 flex items-start gap-3 hover:border-ink-600 transition-colors">
    <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${TONES[tone]}`}>{icon}</span>
    <div className="min-w-0">
      <p className="text-ink-500 text-xs">{label}</p>
      <p className="text-2xl font-bold font-mono tabular-nums text-ink-50 leading-tight">{value}</p>
      {sub && <p className="text-ink-500 text-xs mt-0.5 truncate">{sub}</p>}
    </div>
  </div>
);
